<script setup lang="ts">
import BaseIcon from '@/components/ui/BaseIcon.vue';
import type { IconName } from '@/libs/icons';

withDefaults(
  defineProps<{
    /** Renders as a link instead of a button */
    href?: string;
    /** Lucide icon name */
    icon?: IconName;
    label: string;
    /** Lucide icon name; `check` is tinted green (active language) */
    trailingIcon?: IconName;
  }>(),
  {
    href: undefined,
    icon: undefined,
    trailingIcon: undefined,
  },
);
</script>

<template>
  <component
    :is="href ? 'a' : 'button'"
    class="settings-row"
    :href="href"
    :type="href ? undefined : 'button'"
  >
    <BaseIcon v-if="icon" :name="icon" class="settings-row__icon" />
    <span class="settings-row__label">{{ label }}</span>
    <BaseIcon
      v-if="trailingIcon"
      :name="trailingIcon"
      class="settings-row__trailing"
      :class="{ 'settings-row__trailing--check': trailingIcon === 'check' }"
    />
  </component>
</template>

<style lang="scss">
.settings-row {
  align-items: center;
  background: none;
  border: none;
  // <a> rows default to content-box: width 100% + padding would overflow.
  box-sizing: border-box;
  color: $color-neutral-highest;
  cursor: pointer;
  display: flex;
  gap: $spacing-3;
  padding: $spacing-4 $spacing-3;
  text-align: left;
  text-decoration: none;
  width: 100%;

  &:active {
    background-color: $color-neutral-lower;
  }

  &__icon {
    flex-shrink: 0;
    font-size: 24px;
  }

  &__label {
    flex: 1;
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__trailing {
    flex-shrink: 0;
    font-size: 24px;

    &--check {
      color: $color-primary;
    }
  }
}
</style>
